import React from 'react';
import {
    Hash,
    Mail,
    MessageCircle,
    MessageSquare,
    MessagesSquare,
    MessageSquareDashed,
    PanelTop,
    Archive,
} from 'lucide-react';
import {
    CHANNEL_HEALTH_LABELS,
    channelHealthResult,
    googleChatModeResults,
} from '../utils/channelHealthDisplay';

// Named against the design tokens the Console actually defines. The previous
// spelling pointed at --color-tag-green-ink and two siblings, none of which
// exist in design-tokens.css. An undefined variable makes the whole background
// declaration invalid, so a healthy channel drew a dot with no colour at all.
// Nobody caught it because every channel currently reads "unknown", and that one
// case happened to name a token that does exist.
const HEALTH_COLORS = {
    passed: 'var(--color-success)',
    failed: 'var(--color-error)',
    off: 'var(--color-text-disabled)',
};

/**
 * The colour of a channel's icon button, and why it has that colour.
 *
 * One rule covers both facts a person needs at a glance. A fault outranks
 * everything else: a channel can be switched on for the organization and still
 * be failing, and green would hide that. Below a fault, green means the
 * organization may send on this channel and grey means it may not.
 *
 * Grey is also what an unknown answer gets. Unknown is not the same as working,
 * and green for "we have not checked" is the one colour that would mislead.
 */
export function channelStateColor({ enabled, health }) {
    const result = channelHealthResult({ enabled, runtime_health: health });
    return { color: HEALTH_COLORS[result], state: result };
}

// The same icons the Channels screen shows in its Bindings rail, so a channel
// looks the same in both places. They are keyed by the ChannelGate token, not by
// the Console inventory id, because the token is what the two screens share.
const CHANNEL_ICONS = {
    email: Mail,
    whatsapp: MessageCircle,
    sms: MessageSquare,
    googlechat: MessagesSquare,
    msteams: PanelTop,
    slack: Hash,
    messenger: MessagesSquare,
    rcs: MessageSquareDashed,
};

// Every listed channel can lead to the Channels context. Binding-section
// selection happens only inside that context, through its top Bindings command.
const BINDINGS_SECTIONS = new Set(['email', 'whatsapp', 'messenger', 'sms', 'rcs', 'googlechat', 'msteams', 'slack']);

/**
 * One channel in the Channel Status panel: a health light, the channel name, and
 * the channel's own icon. The icon opens the Channels context without selecting
 * a binding section.
 */
function GoogleChatModeIcon({ mode, result, reason, onOpenBindings }) {
    const label = CHANNEL_HEALTH_LABELS[result];
    return (
        <button
            type="button"
            onClick={() => onOpenBindings?.()}
            data-channel-mode={mode.toLowerCase()}
            data-channel-state={result}
            aria-label={`Open Channels from Google Chat ${mode} status: ${label}`}
            title={reason || `Google Chat ${mode}: ${label}`}
            style={{
                border: 0,
                background: 'transparent',
                padding: '2px 3px 5px',
                display: 'inline-flex',
                position: 'relative',
                color: HEALTH_COLORS[result],
                cursor: 'pointer',
                flexShrink: 0,
            }}
        >
            <MessagesSquare size={14} strokeWidth={1.5} aria-hidden="true" />
            <span aria-hidden="true" style={{
                position: 'absolute',
                right: 0,
                bottom: 0,
                padding: '0 1px',
                borderRadius: 2,
                background: 'var(--color-bg-panel)',
                color: 'currentColor',
                fontSize: 6,
                fontWeight: 700,
                lineHeight: 1,
                letterSpacing: '-0.2px',
            }}>{mode}</span>
        </button>
    );
}

export default function ChannelStatusRow({ channel, control, onOpenBindings }) {
    const enabled = control?.enabled;
    const result = channelHealthResult(control);
    const googleChatModes = channel.token === 'googlechat'
        ? googleChatModeResults(control)
        : null;
    const dotColor = HEALTH_COLORS[result];
    const Icon = CHANNEL_ICONS[channel.token] || Archive;
    const hasBindings = BINDINGS_SECTIONS.has(channel.token);
    const { color: buttonColor, state: buttonState } = channelStateColor({
        enabled,
        health: control?.runtime_health || 'unknown',
    });

    return (
        <div style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0',
            borderBottom: '1px solid #F1F3F4',
        }}>
            {googleChatModes ? (
                <span aria-hidden="true" style={{ width: 10, height: 10, flexShrink: 0 }} />
            ) : (
                <span
                    title={result === 'failed' && control?.runtime_health_reason
                        ? control.runtime_health_reason
                        : `Channel health: ${CHANNEL_HEALTH_LABELS[result]}`}
                    style={{
                        width: 10, height: 10, borderRadius: '50%', background: dotColor,
                        flexShrink: 0,
                    }}
                />
            )}
            <span style={{
                flex: 1, fontSize: 12, display: 'flex', flexDirection: 'column',
                fontWeight: enabled === true ? 500 : 400,
                color: enabled === true ? '#3C4043' : '#9AA0A6',
                minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
                <span>{channel.name}</span>
                <span style={{ fontSize: 10, fontWeight: 400 }}>
                    {googleChatModes
                        ? `DCM ${CHANNEL_HEALTH_LABELS[googleChatModes.dcm]} · CRM ${CHANNEL_HEALTH_LABELS[googleChatModes.crm]}`
                        : `Status: ${CHANNEL_HEALTH_LABELS[result]}`}
                </span>
            </span>
            {googleChatModes ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                    <GoogleChatModeIcon
                        mode="DCM"
                        result={googleChatModes.dcm}
                        reason={control?.runtime_health_reason || ''}
                        onOpenBindings={() => onOpenBindings?.(channel)}
                    />
                    <GoogleChatModeIcon
                        mode="CRM"
                        result={googleChatModes.crm}
                        reason={control?.runtime_health_reason || ''}
                        onOpenBindings={() => onOpenBindings?.(channel)}
                    />
                </span>
            ) : <button
                type="button"
                onClick={() => hasBindings && onOpenBindings?.(channel)}
                disabled={!hasBindings}
                data-channel-state={buttonState}
                aria-label={hasBindings
                    ? `Open Channels from ${channel.name} status`
                    : `${channel.name} has no bindings settings`}
                title={hasBindings
                    ? `Open Channels from ${channel.name} status`
                    : `${channel.name} has no bindings settings`}
                style={{
                    border: 0,
                    background: 'transparent',
                    padding: 2,
                    display: 'inline-flex',
                    alignItems: 'center',
                    color: hasBindings ? buttonColor : 'var(--color-text-disabled)',
                    cursor: hasBindings ? 'pointer' : 'default',
                    flexShrink: 0,
                }}
            >
                <Icon size={14} strokeWidth={1.5} aria-hidden="true" />
            </button>}
        </div>
    );
}
